/* eslint @typescript-eslint/strict-boolean-expressions:0 */
// import log from 'loglevel'
import {
  compact,
  concat,
  flatten,
  head,
  includes,
  isEmpty,
  isNil,
  isNumber,
  last as lastLodash,
  map,
  max,
  min,
  range,
  reduce,
  reverse,
  size,
  slice,
  sortBy,
  split,
  // sum,
  template,
  toString
} from 'lodash/fp'
import {
  addDays,
  addMonths,
  Day,
  eachDayOfInterval,
  eachMinuteOfIntervalWithOptions,
  eachMonthOfInterval,
  format,
  getDate,
  getDay,
  getDaysInMonth,
  getWeekOfMonthWithOptions,
  getYear,
  isSameMonth,
  parseISO,
  startOfMonth,
  subMonths,
  subYears
} from 'date-fns/fp'
import { endOfMonth } from 'date-fns'
import axios from 'axios'
import {
  type Align,
  type ChartConfiguration,
  type ChartTypeRegistry
} from 'chart.js'
import log from 'loglevel'
import { type ConfigProps, hasConfig } from 'components/Dashboard'
import { CanvasTable, type CTConfig, type CTData } from 'canvas-table'
import { type Anchor } from 'chartjs-plugin-datalabels/types/options'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const defaultBorderColors = [
  ['#EA3324'],
  ['#5C97CE'],
  ['rgba(0, 230, 0, 1)'],
  ['#34692E'],
  ['#71ba17ff'],
  ['#D6FEA4'],
  ['#FFFF55'],
  ['#FFFFD1']
]
const times = map((date: Date) => format('H:mm')(date))(
  eachMinuteOfIntervalWithOptions({ step: 30 })({
    start: parseISO('1970-01-01T00:00'),
    end: parseISO('1970-01-01T23:30')
  })
)
const valueLabel = (classification: string | null | undefined): string =>
  isNil(classification) || classification === '供給' ? '電気使用量' : '売電量'
const yTitle = (
  classification: string | null | undefined
): Record<string, string | boolean> => ({
  display: true,
  text: `${valueLabel(classification)}`,
  color: 'black'
})

const holidaysUrl = 'https://holidays-jp.github.io/api/v1/<%= year %>/date.json'
const dayStrings = ['日', '月', '火', '水', '木', '金', '土']
// <block:plugin:0>
const getOrCreateLegendList = (
  _chart: any,
  id: string,
  configChart: ConfigProps
): HTMLUListElement | null => {
  const legendContainer = document.getElementById(id)
  // legendContainer?.setAttribute('style', "background-color:'rgb(179, 230, 244)';")
  if (isNil(legendContainer)) return null
  const { colors } = configChart
  legendContainer.style.background = hasConfig(configChart, 'colors.canvas')
    ? colors.canvas
    : 'rgb(179, 230, 244)'
  // if (!isNil(width)) legendContainer.style.width = `${width}px`
  legendContainer.style.padding = '1em'
  let listContainer = legendContainer.querySelector('ul')
  if (listContainer) return null
  listContainer = document.createElement('ul')
  listContainer.style.display = 'flex'
  listContainer.style.height = '50px'
  // listContainer.style.flexDirection = 'column'
  listContainer.style.flexDirection = 'row'
  listContainer.style.flexWrap = 'wrap'
  listContainer.style.margin = '0'
  listContainer.style.padding = '0'
  legendContainer.appendChild(listContainer)
  return listContainer
}

const htmlLegendPlugin = (configChart: ConfigProps): any => ({
  id: 'htmlLegend',
  afterRender(chart: any, _args: any, options: any): void {
    const ul = getOrCreateLegendList(chart, options.containerID, configChart)
    if (!isNil(ul)) {
      // while (!isNil(ul.firstChild)) {
      //   ul.firstChild.remove()
      // }
      for (const item of chart.options.plugins.legend.labels.generateLabels(
        chart
      )) {
        if (isNil(item.text)) continue
        const li = document.createElement('li')
        li.style.alignItems = 'center'
        li.style.cursor = 'pointer'
        li.style.display = 'flex'
        li.style.flexDirection = 'row'
        li.style.marginLeft = '10px'
        const boxSpan = document.createElement('span')
        boxSpan.style.background = item.strokeStyle
        boxSpan.style.borderColor = item.strokeStyle
        boxSpan.style.borderWidth = `${item.lineWidth as number}px`
        boxSpan.style.display = 'inline-block'
        boxSpan.style.flexShrink = '0'
        boxSpan.style.height = '12px'
        boxSpan.style.marginRight = '10px'
        boxSpan.style.width = '12px'
        const textContainer = document.createElement('p')
        textContainer.style.color = item.strokeStyle
        textContainer.style.margin = '0'
        textContainer.style.padding = '0'
        textContainer.style.fontSize = '12px'
        textContainer.style.color = 'rgb(0,0,0)'
        textContainer.style.textDecoration = item.hidden ? 'line-through' : ''
        const text = document.createTextNode(item.text)
        textContainer.appendChild(text)
        li.appendChild(boxSpan)
        li.appendChild(textContainer)
        ul.appendChild(li)
      }
    }
  }
})
const legendLine = (enable: boolean): any =>
  enable
    ? {
        fullSize: false,
        align: 'center',
        position: 'bottom',
        labels: {
          usePointStyle: true,
          font: {
            size: 16
          },
          generateLabels: (chart: any) =>
            !isNil(chart.data)
              ? compact(
                  // @ts-expect-error ts(2339)
                  map.convert({ cap: false })((item: any, index: number) => {
                    if (isEmpty(item.data)) return null
                    if (isNil(item.label)) return null
                    return {
                      text: item.label,
                      fillStyle: 'rgb(255,255,255)',
                      strokeStyle: item.borderColor[0],
                      fontColor: item.borderColor[0],
                      lineWidth: 3,
                      lineDash: item.borderDash,
                      pointStyle: 'line',
                      datasetIndex: index,
                      hidden: !(chart.isDatasetVisible(index) as boolean)
                    }
                  })(chart.data.datasets)
                )
              : null
        }
      }
    : { display: false }
const legendBar = (enable: boolean): any =>
  enable
    ? {
        labels: {
          usePointStyle: true,
          font: {
            size: 16
          },
          generateLabels: (chart: any) =>
            !isNil(chart.data)
              ? compact(
                  // @ts-expect-error ts(2339)
                  map.convert({ cap: false })((item: any, index: number) => {
                    if (isEmpty(item.data)) return null
                    return {
                      text: item.label,
                      fillStyle: item.borderColor[0],
                      strokeStyle: item.borderColor[0],
                      fontColor: item.borderColor[0],
                      pointStyle: 'rect',
                      datasetIndex: index,
                      hidden: !(chart.isDatasetVisible(index) as boolean)
                    }
                  })(chart.data.datasets)
                )
              : null
        },
        align: 'center',
        position: 'bottom'
      }
    : { display: false }
// const tempatureAnnotation = (
//   x: number,
//   y: number,
//   content: string[] | undefined,
//   configChart: ConfigProps
// ): { annotations: Record<string, any> } => ({
//   annotations:
//     !isNil(content) && size(content) === 2
//       ? {
//           label1: {
//             type: 'label',
//             position: { x: 'end', y: 'start' },
//             xValue: x,
//             yValue: y,
//             backgroundColor: hasConfig(configChart, 'colors.dailyAnnotation')
//               ? configChart['colors'].dailyAnnotation
//               : 'rgb(235, 205, 179)',
//             content,
//             font: {
//               size: 12
//             }
//           }
//         }
//       : {}
// })
const tempatureAnnotationAsync = (
  x: number,
  y: number,
  content: string[] | undefined,
  position?: { x: string; y: string },
  yScaleId: string = 'y'
  // _configChart: ConfigProps
): Record<string, any> => {
  const canvas = document.createElement('canvas')
  const data: CTData = isNil(content)
    ? [[], []]
    : [split(',')(content[0] ?? ''), split(',')(content[1] ?? '')]
  const fontSize = 16
  const columns: any = [
    { title: '気温', options: { textAlign: 'center' } },
    { title: '最高', options: { textAlign: 'center' } },
    { title: '最低', options: { textAlign: 'center' } }
  ]
  const config: CTConfig = {
    columns,
    data,
    options: {
      borders: {
        column: { color: '000000', width: 1 },
        row: { color: '000000', width: 1 },
        table: { color: '000000', width: 1 },
        header: { color: '000000', width: 1 }
      },
      cell: {
        fontSize,
        background: '#FFF7E1'
      },
      header: {
        fontSize,
        background: '#FFF7E1'
      },
      background: 'rgba(255, 255, 255, 0)',
      fit: true,
      devicePixelRatio: 0.8,
      padding: {
        top: 1
      }
    }
  }
  const ct = new CanvasTable(canvas, config)
  ct.generateTable()
    .then(() => {})
    .catch(log.error)
  return {
    label1: {
      type: 'label',
      position: position ?? { x: 'end', y: 'start' },
      xValue: x,
      yValue: y,
      // backgroundColor: hasConfig(configChart, 'colors.dailyAnnotation')
      //   ? configChart['colors'].dailyAnnotation
      //   : 'rgb(235, 205, 179)',
      content: canvas,
      yScaleId
    }
  }
}
const demandAnnotationAsync = (
  x: number,
  y: number,
  content: string[] | undefined,
  position?: { x: string; y: string }
  // _configChart: ConfigProps
): Record<string, any> => {
  const canvas = document.createElement('canvas')
  const data: CTData = isNil(content)
    ? [
        ['', ''],
        ['', '']
      ]
    : [
        ['デマンド（DM）', content[0]],
        ['契約電力', content[1]]
      ]
  const fontSize = 14
  const columns: any = [
    { title: '', options: { fontWeight: 'bold', textAlign: 'center' } },
    {
      title: '',
      options: { textAlign: 'center' }
    }
  ]
  const config: CTConfig = {
    columns,
    data,
    options: {
      borders: {
        column: { color: '#F9AD66', width: 1 },
        row: { color: '#F9AD66', width: 1 },
        table: { color: '#F9AD66', width: 1 },
        header: { color: '#F9AD66', width: 1 }
      },
      cell: {
        fontSize,
        fontWeight: 'normal',
        background: '#FFF7E1'
      },
      header: false,
      background: 'rgba(255, 255, 255, 0)',
      fit: true,
      devicePixelRatio: 0.8,
      padding: {
        top: 1
      }
    }
  }
  const ct = new CanvasTable(canvas, config)
  ct.generateTable()
    .then(() => {})
    .catch(log.error)
  return {
    label2: {
      type: 'label',
      position: position ?? { x: 'end', y: 'start' },
      xValue: x,
      yValue: y,
      // backgroundColor: hasConfig(configChart, 'colors.dailyAnnotation')
      //   ? configChart['colors'].dailyAnnotation
      //   : 'rgb(235, 205, 179)',
      content: canvas
    }
  }
}
// const createTempatureAnnotation = (
//   tempature:
//     | {
//         date: string | undefined
//         maxTemp: number | null | undefined
//         minTemp: number | null | undefined
//         type?: string
//       }
//     | undefined,
//   compareTo?: string
// ): any | undefined => {
//   if (isNil(tempature)) return undefined
//   const { date, maxTemp, minTemp, type } = tempature
//   if (isNil(date) || isNil(maxTemp) || isNil(minTemp)) return undefined
//   const prefix = !isNil(type) && type !== 'daily' ? '平均' : ''
//   return `${format(
//     isNil(type) || type === 'daily'
//       ? isNil(compareTo) || compareTo === '前週同曜日'
//         ? 'M月d日'
//         : compareTo === '前年同月平均'
//         ? 'yyyy年M月平均'
//         : 'yyyy年平均'
//       : type === 'monthly'
//       ? 'yyyy年M月'
//       : 'yyyy年'
//   )(
//     parseISO(date)
//   )} ${prefix}最高気温 ${maxTemp}°C ${prefix}最低気温 ${minTemp}°C`
// }
const createTempatureAnnotationAsync = (
  tempature:
    | {
        date: string | undefined
        maxTemp: number | null | undefined
        minTemp: number | null | undefined
        type?: string
      }
    | undefined
): any | undefined => {
  if (isNil(tempature)) return ''
  const { date, maxTemp, minTemp, type } = tempature
  if (isNil(date) || isNil(maxTemp) || isNil(minTemp)) return ''
  return `${format(
    isNil(type) || type === 'daily'
      ? 'M月d日'
      : type === 'monthly'
      ? 'y年M月'
      : 'y年'
  )(parseISO(date))},${maxTemp}°C,${minTemp}°C`
}
const backgroundColorPlugin = {
  id: 'customCanvasBackgroundColor',
  beforeDraw: (chart, _args, options) => {
    const { ctx } = chart
    ctx.save()
    ctx.globalCompositeOperation = 'destination-over'
    ctx.fillStyle = options.color || '#99ffff'
    ctx.fillRect(0, 0, chart.width, chart.height)
    ctx.restore()
  }
}
const floorValues = (values: Array<number | null>): Array<number | null> =>
  map((value: number | null) =>
    isNil(value) ? value : Math.floor(value * 10) / 10
  )(values)
export const createDailyChart = (
  date: string,
  values: Array<number | null>,
  lastValues:
    | Array<{ data: Array<number | null>; compareTo: string }>
    | undefined,
  additionalValues: Array<number | null> | undefined,
  targetsValues:
    | Array<{ key: string; data: Array<number | null> | null } | undefined>
    | undefined,
  othersValues: Array<{ data: Array<number | null>; name: string }> | undefined,
  tempature: {
    date: string
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
  },
  lastTempature: {
    date: string | undefined
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
    type: string
  },
  temp: Array<number | null> | undefined,
  // compareTo: string,
  demand:
    | {
        amount: number
        date: string
        latest: { amount: number | undefined; date: string | undefined }
      }
    | null
    | undefined,
  configChart: ConfigProps,
  classification: string | null | undefined,
  scrollRef: any
): ChartConfiguration => {
  const { yMinValue, colors } = configChart
  const last = flatten(
    map((item: { data: Array<number | null> }) => item.data)(lastValues)
  )
  const allValues = flatten([
    values,
    last,
    additionalValues,
    flatten(
      compact(
        map((item: { data: Array<number | null> | null } | undefined) =>
          isNil(item) ? undefined : item.data
        )(targetsValues ?? [])
      )
    ),
    flatten(
      map((item: { data: Array<number | null> }) => item.data)(
        othersValues ?? []
      )
    )
  ])
  const yMax = max(allValues) as number
  const xMax = max([
    size(values),
    ...map((item: { data: Array<number | null> }) => size(item.data))(
      lastValues
    )
  ]) as number
  const yMin = yMinValue ?? (min(allValues) as number)
  // const margin = 150 / (320 / (yMax - yMin))
  const stepSize = (yMax - yMin) / 1.5
  const content = compact([
    createTempatureAnnotationAsync(tempature),
    createTempatureAnnotationAsync(lastTempature)
  ])
  let demandContent
  if (!isNil(demand)) {
    const { amount, latest } = demand
    if (!isNil(latest.amount)) {
      demandContent = [
        `${latest.amount.toLocaleString()}kW`,
        `${amount}kW（使用率${Math.floor((latest.amount / amount) * 100)}%）`
      ]
    }
  }
  const margin = size(content) === 0 ? 0 : stepSize
  const annotationY = (Math.floor((yMax + margin) / 5) + 1) * 5
  // +
  // (((yMax + margin) * 10) % (stepSize * 10) === 0
  //   ? 0
  //   : stepSize - (((yMax + margin) * 10) % (stepSize * 10)))
  const tempMax = max(temp) as number
  const tempMin = min(temp) as number
  const tempStepSize = (tempMax - tempMin) / 4
  const tempAnnotationY = tempMax + tempStepSize
  // const above = hasConfig(configChart, 'colors.above')
  //   ? configChart['colors'].above
  //   : 'rgb(150, 200, 200)'
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.borderColors
    : defaultBorderColors
  const borderWidth = 6
  const options: ChartConfiguration = {
    type: 'line' as keyof ChartTypeRegistry,
    data: {
      labels: times,
      datasets: compact(
        flatten([
          {
            // label: `${valueLabel(classification)}`,
            label: format('y年M月d日')(parseISO(date)),
            data: values,
            // fill:
            //   size(lastValues) === 1
            //     ? {
            //         target: '1',
            //         above,
            //         below: 'rgb(0, 0, 0, 0)'
            //       }
            //     : undefined,
            borderColor: borderColors[0],
            borderWidth,
            tension: 0.1
          },
          isNil(lastValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })(
                (
                  values:
                    | { data: Array<number | null>; compareTo: string }
                    | undefined,
                  index: number
                ) => {
                  return isNil(values)
                    ? {}
                    : {
                        label: `${values.compareTo}`,
                        data: values.data,
                        borderColor: borderColors[1][index],
                        borderWidth,
                        tension: 0.1
                      }
                }
              )(lastValues),
          isNil(additionalValues)
            ? undefined
            : {
                label: `当月平均`,
                data: additionalValues,
                borderDash: [5, 5],
                borderWidth,
                borderColor: borderColors[2][0],
                tension: 0.1
              },
          isNil(targetsValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })(
                (
                  targetValues:
                    | { key: string; data: Array<number | null> }
                    | undefined,
                  index: number
                ) => {
                  const { key, data } = targetValues ?? {
                    key: '',
                    data: undefined
                  }
                  return isNil(data)
                    ? {}
                    : {
                        label: `${key}平均`,
                        data,
                        borderDash: [5, 5],
                        borderWidth,
                        borderColor: borderColors[3][index],
                        tension: 0.1
                      }
                }
              )(targetsValues),
          isNil(othersValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })(
                (
                  otherValues:
                    | { data: Array<number | null>; name: string }
                    | undefined,
                  index: number
                ) => {
                  return isNil(otherValues)
                    ? {}
                    : {
                        label: `${otherValues.name}`,
                        data: otherValues.data,
                        borderColor: borderColors[1][index % 3],
                        borderWidth,
                        tension: 0.1
                      }
                }
              )(othersValues),
          isNil(temp)
            ? undefined
            : {
                label: `気温推移`,
                data: temp,
                borderDash: [5, 5],
                borderWidth,
                borderColor: borderColors[2][0],
                tension: 0.1,
                yAxisID: 'temp'
              }
        ])
      )
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: 'black'
          }
        },
        y: {
          // suggestedMax: annotationY
          max: annotationY,
          min: yMin,
          title: yTitle(classification),
          ticks: {
            // count: annotationY / 4
            stepSize: 5
          }
        },
        temp: {
          max: tempAnnotationY,
          suggestedMax: tempMin,
          // type: 'linear',
          display: !isNil(temp),
          position: 'right',
          ticks: {
            // count: 7
            stepSize: 5
          },
          grid: {
            display: false
          },
          title: {
            display: true,
            text: '気温',
            color: 'black'
          }
        }
      },
      plugins: {
        // @ts-expect-error ts(2553)
        htmlLegend: {
          containerID: 'legend-container'
        },
        legend: legendLine(true),
        annotation: {
          annotations: !isNil(demandContent)
            ? {
                ...tempatureAnnotationAsync(xMax - 1, annotationY, content),
                ...demandAnnotationAsync(xMax - 12, annotationY, demandContent)
              }
            : tempatureAnnotationAsync(xMax - 1, annotationY, content)
        },
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: false
        }
      }
    },
    plugins: [
      backgroundColorPlugin,
      htmlLegendPlugin(configChart),
      {
        id: 'afterRender',
        afterRender: () => {
          if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
            scrollRef.current.scrollIntoView()
          }
        }
      }
    ]
  }
  return options
}
export const createWeeklyChart = async (
  date: string,
  values: Array<number | null>,
  lastValues: Array<number | null>,
  compareTo: string,
  configChart: ConfigProps,
  classification: string | null | undefined
): Promise<ChartConfiguration> => {
  const { colors } = configChart
  const { data: holidays } = await axios.get(
    template(holidaysUrl)({ year: getYear(parseISO(date)) })
  )
  const duration = map((date: Date) => {
    const day = getDate(date)
    return [dayStrings[getDay(date)], day]
  })(
    eachDayOfInterval({
      start: parseISO(date),
      end: addDays(6)(parseISO(date))
    })
  )
  const above = hasConfig(configChart, 'colors.above')
    ? colors.above
    : 'rgb(150, 200, 200)'
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.borderColors
    : [['rgba(75, 192, 192)'], ['rgba(192, 192, 75)']]
  const dayColors = {
    日: 'red',
    月: '',
    火: '',
    水: '',
    木: '',
    金: '',
    土: 'blue'
  }
  const options: ChartConfiguration = {
    type: 'line' as keyof ChartTypeRegistry,
    data: {
      labels: duration,
      datasets: [
        {
          label: `${compareTo}の${valueLabel(classification)}`,
          data: floorValues(lastValues),
          borderDash: [5, 5],
          borderColor: borderColors[1],
          tension: 0.1
        },
        {
          label: `${valueLabel(classification)}`,
          data: floorValues(values),
          fill: {
            target: '1',
            above,
            below: 'rgb(0, 0, 0, 0)'
          },
          borderColor: borderColors[0],
          tension: 0.1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: (context: any) => {
              const day = `${format('yyyy-MM')(parseISO(date))}-${(
                '0' + toString((context.tick.value as number) + 1)
              ).slice(-2)}`
              if (!isNil(holidays[day])) {
                return 'red'
              } else {
                return dayColors[
                  context.tick.label[0] as keyof typeof dayColors
                ]
              }
            }
          }
        },
        y: {
          title: yTitle(classification)
        }
      },
      plugins: {
        legend: legendLine(true),
        // @ts-expect-error ts(2553)
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: false
        }
      }
    },
    plugins: [backgroundColorPlugin]
  }
  return options
}
export const createMonthlyChart = async (
  date: string,
  values: Array<number | null>,
  lastValues:
    | Array<{ data: Array<number | null>; compareTo: string }>
    | undefined,
  targetsValues:
    | Array<{ key: string; data: Array<number | null> | null } | undefined>
    | undefined,
  othersValues: Array<{ data: Array<number | null>; name: string }> | undefined,
  tempature: {
    date: string
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
  },
  lastTempature: {
    date: string | undefined
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
    type: string
  },
  // compareTo: string,
  maxTemp: Array<number | null> | undefined,
  minTemp: Array<number | null> | undefined,
  configChart: ConfigProps,
  scrollRef: any
): Promise<ChartConfiguration> => {
  const { yMinValue, colors } = configChart
  const last = flatten(
    map((item: { data: Array<number | null> }) => item.data)(lastValues)
  )
  const allValues = flatten([
    values,
    last,
    flatten(
      compact(
        map((item: { data: Array<number | null> | null } | undefined) =>
          isNil(item) ? undefined : item.data
        )(targetsValues ?? [])
      )
    ),
    flatten(
      map((item: { data: Array<number | null> }) => item.data)(
        othersValues ?? []
      )
    )
  ])
  const yMax = max(allValues) as number
  const xMax = max([
    size(values),
    ...map((item: { data: Array<number | null> }) => size(item.data))(
      lastValues
    )
  ]) as number
  const stepSize = yMax / 1.3
  const margin = stepSize
  const annotationY = yMax + margin
  const yMin = yMinValue ?? (min(allValues) as number)
  // const margin = 150 / (320 / (yMax - yMin))
  // +
  // (((yMax + margin) * 10) % (stepSize * 10) === 0
  //   ? 0
  //   : stepSize - (((yMax + margin) * 10) % (stepSize * 10)))
  // @ts-expect-error ts(2339)
  const sumValues = map.convert({ cap: false })(
    (value: number | null, index: number) => {
      return isNil(value)
        ? null
        : index === 0
        ? value
        : value +
          reduce(
            (sum: number, value: number | null) => sum + (value ?? 0),
            0
          )(slice(0, index)(values))
    }
  )(values)
  const days = getDaysInMonth(parseISO(date))
  let calcSumValues
  if (
    isSameMonth(parseISO(date))(Date.now()) &&
    size(compact(sumValues)) < days
  ) {
    const count = size(compact(sumValues))
    const b = head(sumValues) as number
    const lastSumValue = lastLodash(compact(sumValues)) as number
    const a = (lastSumValue - b) / (count - 1)
    const calc = (x: number): number => a * x + b
    calcSumValues = map((index: number) =>
      index < count
        ? index <= count - 1
          ? sumValues[index]
          : null
        : calc(index)
    )(range(0)(days))
  }
  const sumLastValues = isNil(lastValues)
    ? undefined
    : map(
        (
          values: { data: Array<number | null>; compareTo: string } | undefined
        ) => {
          return isNil(values)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })((value: number, index: number) => {
                return index === 0
                  ? value
                  : value +
                      reduce(
                        (sum: number, value: number | null) =>
                          sum + (value ?? 0),
                        0
                      )(slice(0, index)(values.data))
              })(values.data)
        }
      )(lastValues)
  const sumTargetsValues = isNil(targetsValues)
    ? undefined
    : map(
        (
          values: { key: string; data: Array<number | null> | null } | undefined
        ) => {
          return isNil(values)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })(
                (value: number | null, index: number) => {
                  return isNil(value)
                    ? null
                    : index === 0
                    ? value
                    : value +
                      reduce(
                        (sum: number, value: number | null) =>
                          sum + (value ?? 0),
                        0
                      )(slice(0, index)(values.data))
                }
              )(values.data)
        }
      )(targetsValues)
  let calcSumTargetsValues
  if (isSameMonth(parseISO(date))(Date.now())) {
    calcSumTargetsValues = map((sumValues: any) => {
      if (size(compact(sumValues)) < days) {
        const count = size(compact(sumValues))
        const b = head(sumValues) as number
        const lastSumValue = lastLodash(compact(sumValues)) as number
        const a = (lastSumValue - b) / (count - 1)
        const calc = (x: number): number => a * x + b
        return map((index: number) =>
          index < count
            ? index <= count - 1
              ? sumValues[index]
              : null
            : calc(index)
        )(range(0)(days))
      } else {
        return undefined
      }
    })(sumTargetsValues)
  }
  const sumOthersValues = isNil(othersValues)
    ? undefined
    : map(
        (
          otherValues: { data: Array<number | null>; name: string } | undefined
        ) => {
          return isNil(otherValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })(
                (value: number | null, index: number) => {
                  return isNil(value)
                    ? null
                    : index === 0
                    ? value
                    : value +
                      reduce(
                        (sum: number, value: number | null) =>
                          sum + (value ?? 0),
                        0
                      )(slice(0, index)(otherValues.data))
                }
              )(otherValues.data)
        }
      )(othersValues)
  let calcSumOthersValues
  if (isSameMonth(parseISO(date))(Date.now())) {
    calcSumOthersValues = map((sumValues: any) => {
      if (size(compact(sumValues)) < days) {
        const count = size(compact(sumValues))
        const b = head(sumValues) as number
        const lastSumValue = lastLodash(compact(sumValues)) as number
        const a = (lastSumValue - b) / (count - 1)
        const calc = (x: number): number => a * x + b
        return map((index: number) =>
          index < count
            ? index <= count - 1
              ? sumValues[index]
              : null
            : calc(index)
        )(range(0)(days))
      } else {
        return undefined
      }
    })(sumOthersValues)
  }
  const allSumValues = flatten([
    sumValues,
    calcSumValues,
    flatten(sumLastValues),
    flatten(sumTargetsValues),
    flatten(sumOthersValues),
    flatten(calcSumTargetsValues),
    flatten(calcSumOthersValues)
  ])
  const sumMax = max(allSumValues) as number
  const sumStepSize = (sumMax - 0) / 1.3
  const sumAnnotationY = sumMax + sumStepSize
  const tempMax = max(concat(maxTemp)(minTemp)) as number
  // const tempMin = min(concat(maxTemp)(minTemp)) as number
  const tempStepSize = tempMax / 1.3
  const tempMargin = tempStepSize
  const tempAnnotationY = isNil(tempMax) ? undefined : tempMax + tempMargin
  const content = compact([
    createTempatureAnnotationAsync({ ...tempature, type: 'monthly' }),
    createTempatureAnnotationAsync(lastTempature)
  ])
  const { data: holidays } = await axios.get(
    template(holidaysUrl)({ year: getYear(parseISO(date)) })
  )
  const duration = map((date: Date) => {
    const day = getDate(date)
    return [dayStrings[getDay(date)], day]
  })(
    eachDayOfInterval({
      start: parseISO(date),
      end: endOfMonth(parseISO(date))
    })
  )
  // const above = hasConfig(configChart, 'colors.above')
  //   ? configChart['colors'].above
  //   : 'rgb(150, 200, 200)'
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.borderColors
    : defaultBorderColors
  const dayColors = {
    日: 'red',
    月: '',
    火: '',
    水: '',
    木: '',
    金: '',
    土: 'blue'
  }
  const borderWidth = 6
  const options: ChartConfiguration = {
    type: 'bar' as keyof ChartTypeRegistry,
    data: {
      labels: duration,
      datasets: compact(
        flatten([
          {
            // label: `${valueLabel(classification)}`,
            label: format('y年M月')(parseISO(date)),
            data: values,
            borderColor: borderColors[4][0],
            backgroundColor: borderColors[4][0],
            barPercentage: 8,
            categoryPercentage: -0.4,
            order: 4
          },
          isNil(calcSumValues)
            ? undefined
            : {
                type: 'line' as keyof ChartTypeRegistry,
                data: calcSumValues,
                borderColor: borderColors[0],
                borderDash: [4, 4],
                pointStyle: false,
                borderWidth,
                tension: 0.1,
                yAxisID: 'sum',
                order: 2
              },
          {
            type: 'line' as keyof ChartTypeRegistry,
            data: sumValues,
            borderColor: borderColors[0],
            pointBorderWidth: 2,
            borderWidth,
            tension: 0.1,
            yAxisID: 'sum',
            order: 1
          },
          isNil(lastValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })(
                (
                  values:
                    | { data: Array<number | null>; compareTo: string }
                    | undefined,
                  index: number
                ) => {
                  return isNil(values)
                    ? {}
                    : {
                        label: `${values.compareTo}`,
                        data: values.data,
                        borderColor: borderColors[4][index + 1],
                        backgroundColor: borderColors[4][index + 1],
                        tension: 0.1,
                        barPercentage: 8,
                        categoryPercentage: 0.4,
                        order: 5
                      }
                }
              )(lastValues),
          isNil(sumLastValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })((values: number[], index: number) => {
                return isNil(values)
                  ? {}
                  : {
                      type: 'line' as keyof ChartTypeRegistry,
                      data: values,
                      yAxisID: 'sum',
                      borderColor: borderColors[1][index],
                      pointBorderWidth: 2,
                      borderWidth,
                      tension: 0.1,
                      order: 3
                    }
              })(sumLastValues),
          isNil(targetsValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })(
                (
                  targetValues:
                    | { key: string; data: Array<number | null> }
                    | undefined,
                  index: number
                ) => {
                  const { key, data } = targetValues ?? {
                    key: '',
                    data: undefined
                  }
                  return isNil(data)
                    ? {}
                    : {
                        label: `${key}平均`,
                        data,
                        borderDash: [5, 5],
                        borderColor: borderColors[3][index],
                        backgroundColor: borderColors[3][index],
                        tension: 0.1,
                        maxBarThickness: 5,
                        barThickness: 'flex'
                      }
                }
              )(targetsValues),
          isNil(calcSumTargetsValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })((values: number[], index: number) => {
                return isNil(values)
                  ? {}
                  : {
                      type: 'line' as keyof ChartTypeRegistry,
                      data: values,
                      yAxisID: 'sum',
                      borderColor: borderColors[3][index],
                      borderDash: [4, 4],
                      pointStyle: false,
                      borderWidth,
                      tension: 0.1
                    }
              })(calcSumTargetsValues),
          isNil(sumTargetsValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })((values: number[], index: number) => {
                return isNil(values)
                  ? {}
                  : {
                      type: 'line' as keyof ChartTypeRegistry,
                      data: values,
                      yAxisID: 'sum',
                      borderColor: borderColors[3][index],
                      borderWidth,
                      tension: 0.1
                    }
              })(sumTargetsValues),
          isNil(othersValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })(
                (
                  otherValues:
                    | { data: Array<number | null>; name: string }
                    | undefined,
                  index: number
                ) => {
                  return isNil(otherValues)
                    ? {}
                    : {
                        label: `${otherValues.name}`,
                        data: otherValues.data,
                        borderColor: borderColors[1][index % 3],
                        backgroundColor: borderColors[1][index % 3],
                        tension: 0.1,
                        maxBarThickness: 5,
                        barThickness: 'flex'
                      }
                }
              )(othersValues),
          isNil(calcSumOthersValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })((values: number[], index: number) => {
                return isNil(values)
                  ? {}
                  : {
                      type: 'line' as keyof ChartTypeRegistry,
                      data: values,
                      yAxisID: 'sum',
                      borderColor: borderColors[1][index % 3],
                      borderDash: [4, 4],
                      pointStyle: false,
                      borderWidth,
                      tension: 0.1
                    }
              })(calcSumOthersValues),
          isNil(sumOthersValues)
            ? undefined
            : // @ts-expect-error ts(2339)
              map.convert({ cap: false })((values: number[], index: number) => {
                return isNil(values)
                  ? {}
                  : {
                      type: 'line' as keyof ChartTypeRegistry,
                      data: values,
                      yAxisID: 'sum',
                      borderColor: borderColors[1][index % 3],
                      borderWidth,
                      tension: 0.1
                    }
              })(sumOthersValues),
          isNil(maxTemp)
            ? undefined
            : {
                type: 'line' as keyof ChartTypeRegistry,
                label: `最高気温`,
                data: maxTemp,
                borderDash: [5, 5],
                borderWidth,
                borderColor: borderColors[2][1],
                tension: 0.1,
                yAxisID: 'temp'
              },
          isNil(minTemp)
            ? undefined
            : {
                type: 'line' as keyof ChartTypeRegistry,
                label: `最低気温`,
                data: minTemp,
                borderDash: [5, 5],
                borderWidth,
                borderColor: borderColors[2][2],
                tension: 0.1,
                yAxisID: 'temp'
              }
        ])
      )
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: (context: any) => {
              const day = `${format('yyyy-MM')(parseISO(date))}-${(
                '0' + toString((context.tick.value as number) + 1)
              ).slice(-2)}`
              if (!isNil(holidays[day])) {
                return 'red'
              } else {
                return dayColors[
                  context.tick.label[0] as keyof typeof dayColors
                ]
              }
            }
          }
        },
        y: {
          // suggestedMax: annotationY - 1,
          max: annotationY,
          position: 'right',
          suggestedMin: yMin,
          title: {
            display: true,
            text: '日別合計電力量(kWh)',
            color: 'black'
          },
          grid: {
            display: false
          },
          ticks: {
            // count: 7
            stepSize: 10
          }
        },
        temp: {
          max: tempAnnotationY,
          display: !isNil(maxTemp) || !isNil(minTemp),
          position: 'right',
          ticks: {
            // count: 7
            stepSize: 5
          },
          grid: {
            display: false
          },
          title: {
            display: true,
            text: '気温',
            color: 'black'
          }
        },
        sum: {
          max: sumAnnotationY,
          ticks: {
            stepSize: 100
            // count: 7
          },
          title: {
            display: true,
            text: '月別合計電力量(kWh)',
            color: 'black'
          }
        }
      },
      plugins: {
        // @ts-expect-error ts(2553)
        htmlLegend: {
          containerID: 'legend-container'
        },
        // legend: legendLine(isNil(targetsValues)),
        legend: legendLine(true),
        annotation: {
          annotations: tempatureAnnotationAsync(xMax - 1, annotationY, content)
        },
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: false
        }
      }
    },
    plugins: [
      backgroundColorPlugin,
      htmlLegendPlugin(configChart),
      {
        id: 'afterRender',
        afterRender: () => {
          if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
            scrollRef.current.scrollIntoView()
          }
        }
      }
    ]
  }
  return options
}
export const createMonthlyChartByDays = (
  date: string,
  values: Array<number | null>,
  configChart: ConfigProps,
  scrollRef: any
): ChartConfiguration => {
  const { colors } = configChart
  const yMax = max(values) as number
  const yMin = Math.floor((min(values) as number) / 10) * 10
  // const yMin = (sum(values) / size(compact(values))) * 0.9
  // const stepSize = (yMax - yMin) / 5
  const stepSize = 10
  const duration = [
    '月曜日',
    '火曜日',
    '水曜日',
    '木曜日',
    '金曜日',
    '土曜日',
    '日曜日'
  ]
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.borderColors
    : defaultBorderColors
  const options: ChartConfiguration = {
    type: 'bar' as keyof ChartTypeRegistry,
    data: {
      labels: duration,
      datasets: [
        {
          label: `${format('y年M月')(parseISO(date))}曜日平均`,
          data: values,
          borderColor: borderColors[0],
          backgroundColor: borderColors[0],
          tension: 0.1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: 'black'
          }
        },
        y: {
          min: yMin,
          suggestedMax: yMax,
          title: {
            display: true,
            text: `平均電力量(kWh)`,
            color: 'black'
          },
          ticks: {
            stepSize
          }
        }
      },
      plugins: {
        // @ts-expect-error ts(2553)
        htmlLegend: {
          containerID: 'legend-container'
        },
        // legend: legendBar(true),
        legend: legendLine(true),
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: false
        }
      }
    },
    plugins: [
      backgroundColorPlugin,
      htmlLegendPlugin(configChart),
      {
        id: 'afterRender',
        afterRender: () => {
          if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
            scrollRef.current.scrollIntoView()
          }
        }
      }
    ]
  }
  return options
}
export const createMonthlyHorizontalChart = async (
  values: Array<number | null>,
  lastValues: Array<number | null>,
  compareTo: string,
  configChart: ConfigProps,
  classification: string | null | undefined
): Promise<ChartConfiguration> => {
  const { colors } = configChart
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.borderColors
    : [['rgba(75, 192, 192)'], ['rgba(192, 192, 75)']]
  const options: ChartConfiguration = {
    type: 'bar' as keyof ChartTypeRegistry,
    data: {
      labels: [''],
      datasets: [
        {
          label: `${valueLabel(classification)}`,
          data: floorValues(values),
          borderColor: borderColors[0],
          backgroundColor: borderColors[0],
          tension: 0.1
        },
        {
          label: `${compareTo}の${valueLabel(classification)}`,
          data: floorValues(lastValues),
          borderColor: borderColors[1],
          backgroundColor: borderColors[1],
          tension: 0.1
        }
      ]
    },
    options: {
      indexAxis: 'y',
      scales: {
        x: {
          grid: {
            display: false
          }
        }
      },
      plugins: {
        legend: legendBar(true),
        // @ts-expect-error ts(2553)
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        }
      }
    },
    plugins: [backgroundColorPlugin]
  }
  return options
}

export const createYearlyChart = (
  date: string,
  values: Array<number | null>,
  lastValues:
    | Array<{ data: Array<number | null>; compareTo: string }>
    | undefined,
  tempature: {
    date: string
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
  },
  lastTempature: {
    date: string | undefined
    maxTemp: number | null | undefined
    minTemp: number | null | undefined
    type: string
  },
  configChart: ConfigProps,
  classification: string | null | undefined,
  showValue: boolean = false,
  scrollRef: any,
  duringThisMonth: boolean
): ChartConfiguration => {
  const { colors } = configChart
  const last = flatten(
    map((item: { data: Array<number | null> }) => item.data)(lastValues)
  )
  const allValues = flatten([values, last])
  const yMax = max(allValues) as number
  const xMax = max([
    size(values),
    ...map((item: { data: Array<number | null> }) => size(item.data))(
      lastValues
    )
  ]) as number
  const yMin = 0
  // const margin = 150 / (320 / (yMax - yMin))
  const stepSize = (yMax - yMin) / 4
  const margin = stepSize
  // +
  // (((yMax + margin) * 10) % (stepSize * 10) === 0
  //   ? 0
  //   : stepSize - (((yMax + margin) * 10) % (stepSize * 10)))
  const content = compact([
    createTempatureAnnotationAsync({ ...tempature, type: 'yearly' }),
    createTempatureAnnotationAsync(lastTempature)
  ])
  const annotationY = !isEmpty(content) ? yMax + margin : yMax
  const duration = map((date: Date) => format('M月')(date))(
    eachMonthOfInterval({
      start: parseISO(date),
      end: addMonths(11)(parseISO(date))
    })
  )
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.borderColors
    : defaultBorderColors
  const color = !duringThisMonth
    ? borderColors[5][0]
    : [
        ...map((_index: number) => borderColors[0])(range(0)(size(values) - 1)),
        borderColors[5][1]
      ]
  const options: ChartConfiguration = {
    type: 'bar' as keyof ChartTypeRegistry,
    data: {
      labels: duration,
      datasets: compact(
        flatten([
          isNil(lastValues)
            ? undefined
            : map(
                (
                  values:
                    | { data: Array<number | null>; compareTo: string }
                    | undefined
                ) => {
                  return isNil(values)
                    ? undefined
                    : {
                        label: format('y年')(subYears(1)(parseISO(date))),
                        data: map((item: any) =>
                          !isNumber(item) ? null : item
                        )(values.data),
                        borderColor: borderColors[1][0],
                        backgroundColor: borderColors[1][0],
                        tension: 0.1,
                        datalabels: {
                          anchor: 'end' as Anchor,
                          color: borderColors[1],
                          align: 'start' as Align,
                          offset: 20,
                          backgroundColor: 'rgb(255, 255, 255)',
                          formatter: (value: number | null) =>
                            !isNil(value)
                              ? Math.floor(value).toLocaleString()
                              : null
                        }
                      }
                }
              )(lastValues),
          {
            // label: `${valueLabel(classification)}`,
            label: format('y年')(parseISO(date)),
            data: map((item: any) => (!isNumber(item) ? null : item))(values),
            borderColor: color,
            backgroundColor: color,
            tension: 0.1,
            datalabels: {
              anchor: 'start' as Anchor,
              color: borderColors[0],
              align: 'end' as Align,
              offset: 20,
              backgroundColor: 'rgb(255, 255, 255)',
              formatter: (value: number | null) =>
                !isNil(value) ? Math.floor(value).toLocaleString() : null
            }
          }
        ])
      )
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: 'black'
          }
        },
        y: {
          // suggestedMax: annotationY - 1,
          max: annotationY,
          suggestedMin: yMin,
          title: yTitle(classification),
          ticks: {
            // count: 7
            stepSize: 100
          }
        }
      },
      plugins: {
        // @ts-expect-error ts(2553)
        htmlLegend: {
          containerID: 'legend-container'
        },
        // legend: legendBar(isNil(targetsValues)),
        legend: legendBar(true),
        annotation: !isEmpty(content)
          ? {
              annotations: tempatureAnnotationAsync(
                xMax - 1,
                annotationY,
                content
              )
            }
          : undefined,
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: showValue,
          formatter: (value: number) => Math.floor(value).toLocaleString()
        }
      }
    },
    plugins: [
      backgroundColorPlugin,
      htmlLegendPlugin(configChart),
      {
        id: 'afterRender',
        afterRender: () => {
          if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
            scrollRef.current.scrollIntoView()
          }
        }
      }
    ]
  }
  return options
}
export const createDemandYearlyChart = (
  date: string,
  values: Array<number | null>,
  demand: number | null | undefined,
  configChart: ConfigProps,
  scrollRef: any
): ChartConfiguration => {
  const { yMinValue, colors } = configChart
  // const yMax = max(values) as number
  const yMin = yMinValue ?? (min(values) as number)
  // const margin = 150 / (320 / (yMax - yMin))
  // const stepSize = (yMax - yMin) / 5
  const stepSize = 10
  const duration = map((date: Date) => format('M月')(date))(
    eachMonthOfInterval({
      start: subMonths(11)(parseISO(date)),
      end: parseISO(date)
    })
  )
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.demand.borderColors
    : defaultBorderColors
  const labels = ['dummy', ...duration, 'dummy']
  const options: ChartConfiguration = {
    data: {
      labels,
      datasets: [
        {
          type: 'bar' as keyof ChartTypeRegistry,
          // label: `${valueLabel(classification)}`,
          label: '直近1年の各月最大DM',
          data: [
            0,
            ...map((item: any) => (!isNumber(item) ? null : item))(values)
          ],
          borderColor: borderColors[0],
          backgroundColor: borderColors[0],
          tension: 0.1,
          datalabels: {
            anchor: 'center',
            offset: 20,
            color: 'rgb(255, 255, 255)',
            formatter: (value: number | null) =>
              !isNil(value) ? Math.floor(value).toLocaleString() : null,
            font: {
              size: 20
            }
          },
          xAxisID: 'barX',
          order: 1
        },
        {
          type: 'line',
          label: '直近1年の最大DM',
          data: map((_item: string) => demand ?? 0)(labels),
          borderColor: borderColors[1],
          tension: 0.1,
          pointRadius: 0,
          datalabels: {
            display: false
          },
          xAxisID: 'lineX',
          order: 0
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        barX: {
          min: head(duration),
          max: lastLodash(duration),
          grid: {
            display: false
          },
          ticks: {
            color: 'black'
          }
        },
        lineX: {
          grid: {
            display: false
          },
          ticks: {
            display: false,
            color: 'black'
          }
        },
        y: {
          suggestedMin: yMin,
          title: {
            display: true,
            text: '最大DM発生回数（kW）',
            color: 'black'
          },
          ticks: {
            stepSize
          }
        }
      },
      plugins: {
        // @ts-expect-error ts(2553)
        htmlLegend: {
          containerID: 'legend-container'
        },
        // legend: legendBar(isNil(targetsValues)),
        legend: legendBar(true),
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        }
      }
    },
    plugins: [
      backgroundColorPlugin,
      htmlLegendPlugin(configChart),
      {
        id: 'afterRender',
        afterRender: () => {
          if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
            scrollRef.current.scrollIntoView()
          }
        }
      }
    ]
  }
  return options
}
export const createDemandMonthlyChart = (
  date: string,
  values: Array<number | null>,
  demandArg: number | null | undefined,
  configChart: ConfigProps,
  scrollRef: any
): ChartConfiguration => {
  const { yMinValue, colors } = configChart
  // const yMax = max(values) as number
  const yMin = yMinValue ?? (min(values) as number)
  // const margin = 150 / (320 / (yMax - yMin))
  // const stepSize = (yMax - yMin) / 5
  const stepSize = 10
  const duration = map((date: Date) => format('d日')(date))(
    eachDayOfInterval({
      start: startOfMonth(parseISO(date)),
      end: endOfMonth(parseISO(date))
    })
  )
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.demand.borderColors
    : defaultBorderColors
  const labels = ['dummy', ...duration, 'dummy']
  const demand = demandArg ?? max(values)
  const options: ChartConfiguration = {
    data: {
      labels,
      datasets: [
        {
          type: 'bar' as keyof ChartTypeRegistry,
          // label: `${valueLabel(classification)}`,
          label: `${format('y年M月')(parseISO(date))}の日毎最大DM発生回数`,
          data: [
            0,
            ...map((item: any) => (!isNumber(item) ? null : item))(values)
          ],
          borderColor: borderColors[0],
          backgroundColor: borderColors[0],
          tension: 0.1,
          datalabels: {
            anchor: 'center',
            offset: 20,
            color: 'rgb(255, 255, 255)',
            formatter: (value: number | null) =>
              !isNil(value) ? Math.floor(value).toLocaleString() : null,
            font: {
              size: 20
            }
          },
          xAxisID: 'barX',
          order: 1
        },
        {
          type: 'line',
          label: `${format('y年M月')(parseISO(date))}の最大DM発生回数`,
          data: map((_item: string) => demand ?? 0)(labels),
          borderColor: borderColors[1],
          tension: 0.1,
          pointRadius: 0,
          datalabels: {
            display: false
          },
          xAxisID: 'lineX',
          order: 0
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        barX: {
          min: head(duration),
          max: lastLodash(duration),
          grid: {
            display: false
          },
          ticks: {
            color: 'black'
          }
        },
        lineX: {
          grid: {
            display: false
          },
          ticks: {
            display: false,
            color: 'black'
          }
        },
        y: {
          suggestedMin: yMin,
          title: {
            display: true,
            text: '最大DM発生回数（kW）',
            color: 'black'
          },
          ticks: {
            stepSize
          }
        }
      },
      plugins: {
        // @ts-expect-error ts(2553)
        htmlLegend: {
          containerID: 'legend-container'
        },
        // legend: legendBar(isNil(targetsValues)),
        legend: legendBar(true),
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        }
      }
    },
    plugins: [
      backgroundColorPlugin,
      htmlLegendPlugin(configChart),
      {
        id: 'afterRender',
        afterRender: () => {
          if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
            scrollRef.current.scrollIntoView()
          }
        }
      }
    ]
  }
  return options
}
export const createDemandDailyChart = (
  date: string,
  values: Array<number | null>,
  demand: number | null | undefined,
  configChart: ConfigProps,
  scrollRef: any
): ChartConfiguration => {
  const { yMinValue, colors } = configChart
  // const yMax = max(values) as number
  const yMin = yMinValue ?? (min(values) as number)
  // const margin = 150 / (320 / (yMax - yMin))
  // const stepSize = (yMax - yMin) / 5
  const stepSize = 10
  const duration = map((date: Date) => format('H:mm')(date))(
    eachMinuteOfIntervalWithOptions({ step: 30 })({
      start: parseISO('1970-01-01T00:00'),
      end: parseISO('1970-01-01T23:30')
    })
  )
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.demand.borderColors
    : defaultBorderColors
  const labels = ['dummy', ...duration, 'dummy']
  const options: ChartConfiguration = {
    data: {
      labels,
      datasets: [
        {
          type: 'bar' as keyof ChartTypeRegistry,
          // label: `${valueLabel(classification)}`,
          label: `${format('y年M月d日')(parseISO(date))}の時間毎最大DM発生回数`,
          data: [
            0,
            ...map((item: any) => (!isNumber(item) ? null : item))(values)
          ],
          borderColor: borderColors[0],
          backgroundColor: borderColors[0],
          tension: 0.1,
          datalabels: {
            anchor: 'center',
            offset: 20,
            color: 'rgb(255, 255, 255)',
            formatter: (value: number | null) =>
              !isNil(value) ? Math.floor(value).toLocaleString() : null,
            font: {
              size: 20
            }
          },
          xAxisID: 'barX',
          order: 1
        },
        {
          type: 'line',
          label: `${format('y年M月d日')(parseISO(date))}の最大DM発生回数`,
          data: map((_item: string) => demand ?? 0)(labels),
          borderColor: borderColors[1],
          tension: 0.1,
          pointRadius: 0,
          datalabels: {
            display: false
          },
          xAxisID: 'lineX',
          order: 0
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        barX: {
          min: head(duration),
          max: lastLodash(duration),
          grid: {
            display: false
          },
          ticks: {
            color: 'black'
          }
        },
        lineX: {
          grid: {
            display: false
          },
          ticks: {
            display: false,
            color: 'black'
          }
        },
        y: {
          suggestedMin: yMin,
          title: {
            display: true,
            text: '最大DM発生回数（kW）',
            color: 'black'
          },
          ticks: {
            stepSize
          }
        }
      },
      plugins: {
        // @ts-expect-error ts(2553)
        htmlLegend: {
          containerID: 'legend-container'
        },
        // legend: legendBar(isNil(targetsValues)),
        legend: legendBar(true),
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        }
      }
    },
    plugins: [
      backgroundColorPlugin,
      htmlLegendPlugin(configChart),
      {
        id: 'afterRender',
        afterRender: () => {
          if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
            scrollRef.current.scrollIntoView()
          }
        }
      }
    ]
  }
  return options
}
export const createDemandTrendChart = (
  values: Array<number | null>,
  configChart: ConfigProps,
  scrollRef: any
): ChartConfiguration => {
  const { colors } = configChart
  // const yMax = max(values) as number
  // const yMin = 0
  // const margin = 150 / (320 / (yMax - yMin))
  // const stepSize = (yMax - yMin) / 5
  const stepSize = 5
  const duration = map((date: Date) => format('H:mm')(date))(
    eachMinuteOfIntervalWithOptions({ step: 30 })({
      start: parseISO('1970-01-01T00:00'),
      end: parseISO('1970-01-01T23:30')
    })
  )
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.dashboard.demand.borderColors
    : defaultBorderColors
  const labels = duration
  const tops = slice(
    0,
    5
  )(reverse(sortBy<number>((value) => value)(compact(values))))
  const options: ChartConfiguration = {
    data: {
      labels,
      datasets: [
        {
          type: 'bar' as keyof ChartTypeRegistry,
          // label: `${valueLabel(classification)}`,
          label: '時間毎の最大DM（日単位）発生回数',
          data: map((item: any) => (!isNumber(item) ? null : item))(values),
          borderColor: borderColors[0],
          backgroundColor: map((item: any) =>
            includes(item)(tops) ? borderColors[1] : borderColors[0]
          )(values),
          tension: 0.1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: 'black'
          }
        },
        y: {
          title: {
            display: true,
            text: '発生回数',
            color: 'black'
          },
          ticks: {
            stepSize
          }
        }
      },
      plugins: {
        // @ts-expect-error ts(2553)
        htmlLegend: {
          containerID: 'legend-container'
        },
        // legend: legendBar(isNil(targetsValues)),
        legend: legendBar(true),
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: false
        }
      }
    },
    plugins: [
      backgroundColorPlugin,
      htmlLegendPlugin(configChart),
      {
        id: 'afterRender',
        afterRender: () => {
          if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
            scrollRef.current.scrollIntoView()
          }
        }
      }
    ]
  }
  return options
}
export const createDashboardAllChart = (
  date: string,
  values: number[],
  lastValues: number[] | undefined,
  compareValues: Array<number | string> | undefined,
  configChart: ConfigProps,
  afterRender: () => void,
  scrollRef: any,
  duringThisMonth: boolean
): ChartConfiguration => {
  const { yMinValue, colors } = configChart
  const yMax = max(compareValues ?? []) as number
  // const xMax = max([size(values), size(lastValues)]) as number
  const yMin = yMinValue ?? (min(concat(values)(lastValues ?? [])) as number)
  // const stepSize = (yMax - yMin) / 5
  // const margin = stepSize
  // const annotationY =
  //   yMax +
  //   margin +
  //   (((yMax + margin) * 10) % (stepSize * 10) === 0
  //     ? 0
  //     : stepSize - (((yMax + margin) * 10) % (stepSize * 10)))
  const duration = map((date: Date) => format('y年M月')(date))(
    eachMonthOfInterval({
      start: parseISO(date),
      end: addMonths(11)(parseISO(date))
    })
  )
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.dashboard.borderColors
    : defaultBorderColors
  const color = !duringThisMonth
    ? borderColors[0]
    : [
        ...map((_index: number) => borderColors[0])(range(0)(size(values) - 1)),
        borderColors[3]
      ]
  const options: ChartConfiguration = {
    type: 'bar' as keyof ChartTypeRegistry,
    data: {
      labels: duration,
      datasets: compact(
        flatten([
          isNil(lastValues)
            ? undefined
            : {
                // label: `${values.compareTo}の${valueLabel(
                //   classification
                // )}`,
                label: format('y年度')(subYears(1)(parseISO(date))),
                data: map((item: any) => (!isNumber(item) ? null : item))(
                  lastValues
                ),
                borderColor: borderColors[1],
                backgroundColor: borderColors[1],
                tension: 0.1,
                order: 1
              },
          {
            // label: `${valueLabel(classification)}`,
            label: format('y年度')(parseISO(date)),
            data: map((item: any) => (!isNumber(item) ? null : item))(values),
            borderColor: color,
            backgroundColor: color,
            tension: 0.1,
            order: 2
          },
          isNil(compareValues)
            ? undefined
            : {
                // label: `${values.compareTo}の${valueLabel(
                //   classification
                // )}`,
                type: 'line' as keyof ChartTypeRegistry,
                label: '前年度比',
                data: map((item: any) => (!isNumber(item) ? null : item))(
                  compareValues
                ),
                borderColor: borderColors[2],
                tension: 0.1,
                yAxisID: 'compare',
                order: 0,
                datalabels: {
                  anchor: 'end' as Anchor,
                  align: 'end' as Align,
                  formatter: (value: number | null) =>
                    !isNil(value) ? `${value.toLocaleString()}%` : null,
                  font: {
                    size: 16
                  }
                }
              }
        ])
      )
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: 'black'
          }
        },
        y: {
          // suggestedMax: annotationY - 1,
          suggestedMin: yMin,
          title: {
            display: true,
            text: '(kWh)',
            color: 'black',
            font: {
              size: 16
            }
          }
          // ticks: {
          //   stepSize
          // }
        },
        compare: {
          suggestedMax: yMax + yMax / 10,
          title: {
            display: true,
            text: '(%)',
            color: 'black',
            font: {
              size: 16
            }
          },
          grid: {
            display: false
          },
          position: 'right'
        }
      },
      plugins: {
        // htmlLegend: {
        //   containerID: 'legend-container'
        // },
        legend: legendBar(true),
        // @ts-expect-error ts(2553)
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: (context) => context.datasetIndex === 2
        }
      }
    },
    plugins: [
      backgroundColorPlugin,
      htmlLegendPlugin(configChart),
      {
        id: 'afterRender',
        afterRender: (chart: any) => {
          if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
            scrollRef.current.scrollIntoView()
          }
          if (!chart.$rendered) {
            chart.$rendered = true
            afterRender()
          }
        }
      }
    ]
  }
  return options
}
export const createDashboardOrganizationChart = (
  date: string,
  names: string[],
  values: number[],
  lastValues: number[] | undefined,
  compareValues: Array<number | string> | undefined,
  configChart: ConfigProps,
  afterRender: () => void,
  scrollRef: any,
  duringThisMonth: boolean,
  compareTarget: string | null | undefined,
  weekStartsOn: Day
): ChartConfiguration => {
  const { yMinValue, colors } = configChart
  const yMax = max(compareValues ?? []) as number
  // const xMax = max([size(values), size(lastValues)]) as number
  const yMin = yMinValue ?? (min(concat(values)(lastValues ?? [])) as number)
  // const stepSize = (yMax - yMin) / 5
  // const margin = stepSize
  // const annotationY =
  //   yMax +
  //   margin +
  //   (((yMax + margin) * 10) % (stepSize * 10) === 0
  //     ? 0
  //     : stepSize - (((yMax + margin) * 10) % (stepSize * 10)))
  const borderColors = hasConfig(configChart, 'colors.borderColors')
    ? colors.dashboard.borderColors
    : defaultBorderColors
  const color = !duringThisMonth ? borderColors[0] : borderColors[3]
  const options: ChartConfiguration = {
    type: 'bar' as keyof ChartTypeRegistry,
    data: {
      labels: names,
      datasets: compact(
        flatten([
          isNil(lastValues)
            ? undefined
            : {
                // label: `${values.compareTo}の${valueLabel(
                //   classification
                // )}`,
                label:
                  compareTarget === 'weekly'
                    ? `${format('y年M月')(
                        subYears(1)(parseISO(date))
                      )}第${getWeekOfMonthWithOptions({ weekStartsOn })(
                        parseISO(date)
                      )}週`
                    : format('y年M月')(subYears(1)(parseISO(date))),
                data: map((item: any) => (!isNumber(item) ? null : item))(
                  lastValues
                ),
                borderColor: borderColors[1],
                backgroundColor: borderColors[1],
                tension: 0.1,
                order: 1
              },
          {
            // label: `${valueLabel(classification)}`,
            label:
              compareTarget === 'weekly'
                ? `${format('y年M月')(
                    parseISO(date)
                  )}第${getWeekOfMonthWithOptions({ weekStartsOn })(
                    parseISO(date)
                  )}週`
                : format('y年M月')(parseISO(date)),
            data: map((item: any) => (!isNumber(item) ? null : item))(values),
            borderColor: color,
            backgroundColor: color,
            tension: 0.1,
            order: 2
          },
          isNil(compareValues)
            ? undefined
            : {
                // label: `${values.compareTo}の${valueLabel(
                //   classification
                // )}`,
                type: 'line' as keyof ChartTypeRegistry,
                label: '前年比',
                data: map((item: any) => (!isNumber(item) ? null : item))(
                  compareValues
                ),
                borderColor: borderColors[2],
                tension: 0.1,
                yAxisID: 'compare',
                order: 0,
                datalabels: {
                  anchor: 'end' as Anchor,
                  align: 'end' as Align,
                  formatter: (value: number | null) =>
                    !isNil(value) ? `${value.toLocaleString()}%` : null,
                  font: {
                    size: 16
                  }
                }
              }
        ])
      )
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: 'black'
          }
        },
        y: {
          // suggestedMax: annotationY - 1,
          suggestedMin: yMin,
          title: {
            display: true,
            text: '(kWh)',
            color: 'black',
            font: {
              size: 16
            }
          }
          // ticks: {
          //   stepSize
          // }
        },
        compare: {
          suggestedMax: yMax + yMax / 10,
          title: {
            display: true,
            text: '(%)',
            color: 'black',
            font: {
              size: 16
            }
          },
          grid: {
            display: false
          },
          position: 'right'
        }
      },
      plugins: {
        // @ts-expect-error ts(2553)
        htmlLegend: {
          containerID: 'legend-container'
        },
        // legend: legendBar(isNil(targetsValues)),
        legend: legendBar(true),
        customCanvasBackgroundColor: {
          color: hasConfig(configChart, 'colors.canvas')
            ? colors.canvas
            : 'rgb(179, 230, 244)'
        },
        datalabels: {
          display: (context) => context.datasetIndex === 2
        }
      }
    },
    plugins: [
      backgroundColorPlugin,
      htmlLegendPlugin(configChart),
      {
        id: 'afterRender',
        afterRender: (chart: any) => {
          if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
            scrollRef.current.scrollIntoView()
          }
          if (!chart.$rendered) {
            chart.$rendered = true
            afterRender()
          }
        }
      }
    ]
  }
  return options
}
// export const createDashboardFirstChart = (
//   date: string,
//   names: string[],
//   values: number[],
//   lastValues: number[] | undefined,
//   compareValues: Array<number | string> | undefined,
//   configChart: ConfigProps,
//   afterRender: () => void
// ): ChartConfiguration => {
//   const yMinValue = configChart.yMinValue
//   // const yMax = max(concat(values)(lastValues)) as number
//   // const xMax = max([size(values), size(lastValues)]) as number
//   const yMin = yMinValue ?? (min(concat(values)(lastValues ?? [])) as number)
//   // const stepSize = (yMax - yMin) / 5
//   // const margin = stepSize
//   // const annotationY =
//   //   yMax +
//   //   margin +
//   //   (((yMax + margin) * 10) % (stepSize * 10) === 0
//   //     ? 0
//   //     : stepSize - (((yMax + margin) * 10) % (stepSize * 10)))
//   const borderColors = hasConfig(configChart, 'colors.borderColors')
//     ? configChart['colors'].borderColors
//     : defaultBorderColors
//   const options: ChartConfiguration = {
//     type: 'bar' as keyof ChartTypeRegistry,
//     data: {
//       labels: names,
//       datasets: compact(
//         flatten([
//           isNil(lastValues)
//             ? undefined
//             : {
//                 // label: `${values.compareTo}の${valueLabel(
//                 //   classification
//                 // )}`,
//                 label: format('y年M月')(subYears(1)(parseISO(date))),
//                 data: map((item: any) => (!isNumber(item) ? null : item))(
//                   lastValues
//                 ),
//                 borderColor: borderColors[1],
//                 backgroundColor: borderColors[1],
//                 tension: 0.1
//               },
//           {
//             // label: `${valueLabel(classification)}`,
//             label: format('y年M月')(parseISO(date)),
//             data: map((item: any) => (!isNumber(item) ? null : item))(values),
//             borderColor: borderColors[0],
//             backgroundColor: borderColors[0],
//             tension: 0.1
//           },
//           isNil(compareValues)
//             ? undefined
//             : {
//                 // label: `${values.compareTo}の${valueLabel(
//                 //   classification
//                 // )}`,
//                 type: 'line' as keyof ChartTypeRegistry,
//                 label: '前年比',
//                 data: map((item: any) => (!isNumber(item) ? null : item))(
//                   compareValues
//                 ),
//                 borderColor: borderColors[1],
//                 tension: 0.1,
//                 yAxisID: 'compare'
//               }
//         ])
//       )
//     },
//     options: {
//       responsive: true,
//       maintainAspectRatio: false,
//       scales: {
//         x: {
//           grid: {
//             display: false
//           },
//           ticks: {
//             color: 'black'
//           }
//         },
//         y: {
//           // suggestedMax: annotationY - 1,
//           suggestedMin: yMin,
//           title: {
//             display: true,
//             text: '(kWh)',
//             color: 'black'
//           }
//           // ticks: {
//           //   stepSize
//           // }
//         },
//         compare: {
//           title: {
//             display: true,
//             text: '(%)',
//             color: 'black'
//           },
//           position: 'right'
//         }
//       },
//       plugins: {
//         htmlLegend: {
//           containerID: 'legend-container'
//         },
//         // legend: legendBar(isNil(targetsValues)),
//         legend: legendBar(true),
//         customCanvasBackgroundColor: {
//           color: hasConfig(configChart, 'colors.canvas')
//             ? configChart['colors'].canvas
//             : 'rgb(179, 230, 244)'
//         },
//         datalabels: {
//           display: false
//         }
//       }
//     },
//     plugins: [
//       backgroundColorPlugin,
//       htmlLegendPlugin(configChart),
//       {
//         id: 'afterRender',
//         afterRender: (chart) => {
//           if (!chart.$rendered) {
//             chart.$rendered = true
//             afterRender()
//           }
//         }
//       }
//     ]
//   }
//   return options
// }
// export const createDashboardSecondChart = (
//   date: string,
//   names: string[],
//   values: number[],
//   lastValues: number[] | undefined,
//   compareValues: Array<number | string> | undefined,
//   configChart: ConfigProps,
//   afterRender: () => void
// ): ChartConfiguration => {
//   const yMinValue = configChart.yMinValue
//   // const yMax = max(concat(values)(lastValues)) as number
//   // const xMax = max([size(values), size(lastValues)]) as number
//   const yMin = yMinValue ?? (min(concat(values)(lastValues ?? [])) as number)
//   // const stepSize = (yMax - yMin) / 5
//   // const margin = stepSize
//   // const annotationY =
//   //   yMax +
//   //   margin +
//   //   (((yMax + margin) * 10) % (stepSize * 10) === 0
//   //     ? 0
//   //     : stepSize - (((yMax + margin) * 10) % (stepSize * 10)))
//   const borderColors = hasConfig(configChart, 'colors.borderColors')
//     ? configChart['colors'].borderColors
//     : defaultBorderColors
//   const options: ChartConfiguration = {
//     type: 'bar' as keyof ChartTypeRegistry,
//     data: {
//       labels: names,
//       datasets: compact(
//         flatten([
//           isNil(lastValues)
//             ? undefined
//             : {
//                 // label: `${values.compareTo}の${valueLabel(
//                 //   classification
//                 // )}`,
//                 label: format('y年M月')(subYears(1)(parseISO(date))),
//                 data: map((item: any) => (!isNumber(item) ? null : item))(
//                   lastValues
//                 ),
//                 borderColor: borderColors[1],
//                 backgroundColor: borderColors[1],
//                 tension: 0.1
//               },
//           {
//             // label: `${valueLabel(classification)}`,
//             label: format('y年M月')(parseISO(date)),
//             data: map((item: any) => (!isNumber(item) ? null : item))(values),
//             borderColor: borderColors[0],
//             backgroundColor: borderColors[0],
//             tension: 0.1
//           },
//           isNil(compareValues)
//             ? undefined
//             : {
//                 // label: `${values.compareTo}の${valueLabel(
//                 //   classification
//                 // )}`,
//                 type: 'line' as keyof ChartTypeRegistry,
//                 label: '前年比',
//                 data: map((item: any) => (!isNumber(item) ? null : item))(
//                   compareValues
//                 ),
//                 borderColor: borderColors[1],
//                 tension: 0.1,
//                 yAxisID: 'compare'
//               }
//         ])
//       )
//     },
//     options: {
//       responsive: false,
//       maintainAspectRatio: false,
//       scales: {
//         x: {
//           grid: {
//             display: false
//           },
//           ticks: {
//             color: 'black'
//           }
//         },
//         y: {
//           // suggestedMax: annotationY - 1,
//           suggestedMin: yMin,
//           title: {
//             display: true,
//             text: '(kWh)',
//             color: 'black'
//           }
//           // ticks: {
//           //   stepSize
//           // }
//         },
//         compare: {
//           title: {
//             display: true,
//             text: '(%)',
//             color: 'black'
//           },
//           position: 'right'
//         }
//       },
//       plugins: {
//         htmlLegend: {
//           containerID: 'legend-container'
//         },
//         // legend: legendBar(isNil(targetsValues)),
//         legend: legendBar(true),
//         customCanvasBackgroundColor: {
//           color: hasConfig(configChart, 'colors.canvas')
//             ? configChart['colors'].canvas
//             : 'rgb(179, 230, 244)'
//         },
//         datalabels: {
//           display: false
//         }
//       }
//     },
//     plugins: [
//       backgroundColorPlugin,
//       htmlLegendPlugin(configChart),
//       {
//         id: 'afterRender',
//         afterRender: (chart) => {
//           if (!chart.$rendered) {
//             chart.$rendered = true
//             afterRender()
//           }
//         }
//       }
//     ]
//   }
//   return options
// }
